import { useState, useEffect, useCallback, useRef } from 'react';

export function useIsTextTrimmed(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  val: React.ReactNode,
  w?: string,
  mouseEnter?: boolean
) {
  const [isTextTrimmed, setIsTextTrimmed] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const checkIfTextTrimmed = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const element = wrapperRef.current;
      if (!element) return;

      // Fast visibility check
      const { offsetParent, offsetWidth } = element;
      const isVisible = offsetParent !== null && offsetWidth > 0;

      if (isVisible) {
        setIsTextTrimmed(element.scrollWidth > element.offsetWidth);
      }
    });
  }, [wrapperRef]);

  useEffect(() => {
    // Initial check
    checkIfTextTrimmed();

    // Use ResizeObserver for modern browsers (more efficient than resize event)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(checkIfTextTrimmed);
      if (wrapperRef.current) {
        resizeObserverRef.current.observe(wrapperRef.current);
      }
    } else {
      // Fallback to resize event
      window.addEventListener('resize', checkIfTextTrimmed);
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      } else {
        window.removeEventListener('resize', checkIfTextTrimmed);
      }
    };
  }, [checkIfTextTrimmed, wrapperRef]);

  // Only run check when dependencies actually change
  const prevDeps =
    useRef<[React.ReactNode, string | undefined, boolean | undefined]>();
  useEffect(() => {
    const currentDeps: [
      React.ReactNode,
      string | undefined,
      boolean | undefined
    ] = [val, w, mouseEnter];
    if (!prevDeps.current || !shallowEqual(prevDeps.current, currentDeps)) {
      checkIfTextTrimmed();
    }
    prevDeps.current = currentDeps;
  }, [val, w, mouseEnter, checkIfTextTrimmed]);

  return isTextTrimmed;
}

// Helper function for shallow comparison
function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a) as Array<keyof T>;
  const keysB = Object.keys(b) as Array<keyof T>;
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => Object.is(a[key], b[key]));
}
